import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import ArticleCard from "@/components/ArticleCard";
import TalentCard from "@/components/TalentCard";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import FilmTheatreSubmit from "@/components/FilmTheatreSubmit";
import { Clapperboard, Film, Sparkles } from "lucide-react";

export default function FilmPage() {
  const [articles, setArticles] = useState([]);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    api.get("/articles?category=film").then((r) => setArticles(r.data));
    api.get("/profiles?category=film").then((r) => setProfiles(r.data));
  }, []);

  const comingSoon = articles.find((a) => a.slug?.startsWith("coming-soon-check-out-my-fresh"));
  const restArticles = articles.filter((a) => a.article_id !== comingSoon?.article_id);

  return (
    <div data-testid="film-page">
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden splatter-red">
        <img src="https://images.unsplash.com/photo-1515634928627-2a4e0dae3ddf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtb3ZpZSUyMGRpcmVjdG9yJTIwY2FtZXJhfGVufDB8fHx8MTc4MTIyNzA1MXww&ixlib=rb-4.1.0&q=85" alt="Film" className="w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/60 to-transparent" />
        <div className="absolute inset-0 flex items-end p-6 lg:p-16">
          <div className="max-w-3xl">
            <div className="label text-pfc-gold mb-4 flex items-center gap-2"><Clapperboard size={14} /> Film & Entertainment</div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.85]">
              Underground <span className="gold-text">Films</span>, Independent Voices
            </h1>
            <p className="text-white/70 mt-4 max-w-2xl font-body text-lg">
              A dedicated home for independent filmmakers, actors, directors, and creative productions making cinema outside the system.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
        {/* FEATURED FILM */}
        <div className="mb-16 pfc-card overflow-hidden" data-testid="featured-film">
          <div className="grid lg:grid-cols-[1.4fr_1fr]">
            <div className="bg-black">
              <YouTubeEmbed videoId="oLrpwiJ4gT4" title="Featured Film · Epicscope" />
            </div>
            <div className="p-6 lg:p-10 flex flex-col justify-center">
              <div className="label text-pfc-gold mb-3 flex items-center gap-2"><Sparkles size={14} /> Featured Film</div>
              <h2 className="font-display text-2xl sm:text-3xl text-white uppercase leading-tight">Now Streaming on PFC</h2>
              <p className="text-white/70 mt-4 font-body">
                Selected by the PFC Editorial Team. Direct-from-the-creator cinema — independent voices, original stories, and the future of underground film.
              </p>
              <div className="mt-6 flex gap-3 flex-wrap">
                <a
                  href="https://youtu.be/oLrpwiJ4gT4"
                  target="_blank"
                  rel="noreferrer"
                  className="px-5 py-2.5 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.2em] text-xs hover:bg-pfc-gold-bright"
                  data-testid="featured-film-watch"
                >
                  Watch on YouTube
                </a>
                <Link to="/services" className="px-5 py-2.5 border border-white/20 text-white font-sub uppercase tracking-[0.2em] text-xs hover:border-pfc-gold hover:text-pfc-gold transition">
                  About Epicscope Films
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Film Theatre — filmmaker submissions */}
        <FilmTheatreSubmit />

        <div className="grid lg:grid-cols-4 gap-6 mb-16">
          {[
            { title: "Short Film Premieres", icon: Film, desc: "Exclusive online premieres" },
            { title: "Director Interviews", icon: Clapperboard, desc: "Inside the creative process" },
            { title: "Festival Coverage", icon: Film, desc: "From Sundance to your screen" },
            { title: "Project Funding", icon: Clapperboard, desc: "Spotlight crowdfunding campaigns" },
          ].map((f) => (
            <div key={f.title} className="pfc-card p-6">
              <f.icon className="text-pfc-gold mb-3" size={24} />
              <h3 className="font-sub uppercase tracking-[0.15em] text-white mb-2">{f.title}</h3>
              <p className="text-white/60 text-sm font-body">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* COMING SOON — Check Out My Fresh */}
        {comingSoon && (
          <Link
            to={`/magazine/${comingSoon.slug}`}
            className="group block pfc-card overflow-hidden mb-16 relative grain"
            data-testid="film-coming-soon"
          >
            <div className="grid lg:grid-cols-2">
              <div className="relative aspect-video lg:aspect-auto overflow-hidden">
                <img src={comingSoon.cover_image} alt={comingSoon.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="label bg-pfc-red text-white px-3 py-1.5 flex items-center gap-1.5">
                    <Sparkles size={12} /> Coming Soon
                  </span>
                  <span className="label bg-pfc-gold text-ink-900 px-3 py-1.5">Animated Feature</span>
                </div>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="label text-pfc-gold mb-3">An Epic Scope Film Production</div>
                <h3 className="font-display text-3xl lg:text-4xl text-white uppercase leading-[0.95] group-hover:text-pfc-gold transition">
                  {comingSoon.title}
                </h3>
                <p className="text-white/65 mt-5 font-body">{comingSoon.excerpt}</p>
                <div className="mt-6 label text-white/60">
                  Written & animated by Andre Demarion Brown · Read the announcement →
                </div>
              </div>
            </div>
          </Link>
        )}

        <div className="mb-16">
          <div className="label text-pfc-gold mb-3">Now Featuring</div>
          <h2 className="font-display text-3xl lg:text-4xl text-white uppercase mb-8">The <span className="gold-text">Filmmakers</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {profiles.map((p) => <TalentCard key={p.profile_id} profile={p} />)}
          </div>
        </div>

        <div>
          <div className="label text-pfc-gold mb-3">Reading Room</div>
          <h2 className="font-display text-3xl lg:text-4xl text-white uppercase mb-8">Film <span className="gold-text">Stories</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {restArticles.map((a) => <ArticleCard key={a.article_id} article={a} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
